import { ElNotification } from 'element-plus'

import { useConfirmDialog } from '@/composables/useConfirmDialog'

import { eventEmitter } from '@/utils/events/event-emitter'

import { CommonEvent } from '@/constant/events'

/** 需要顯示 details 的錯誤碼 */
const DETAIL_ERROR_CODES = new Set([400, 450])

/** 需要顯示通知的錯誤碼 */
const NOTIFICATION_ERROR_CODES = new Set([451, 500])

const formatDetails = (details: Record<string, unknown>): string => {
  return Object.entries(details)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join('\n')
}

// 併發請求可能同時噴出多個錯誤，這裡收集一波錯誤訊息（Set 天然去重），
// 用短去抖動視窗彙整成單一彈窗顯示，避免多個彈窗連續跳出/閃爍
const errorMessages = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null
let isErrorDialogOpen = false

/** 收攏一波併發錯誤的去抖動視窗 */
const BURST_FLUSH_DELAY = 50
/**
 * 彈窗關閉後再補顯示排隊錯誤的延遲。
 * 需大於 GlobalDialog 關閉後清除 configs 的 200ms 過場，
 * 否則新開的彈窗會被前一個的清除計時器清成空白。
 */
const REOPEN_AFTER_CLOSE_DELAY = 250

const flushErrorMessages = () => {
  flushTimer = null
  // 已有彈窗開著就先不開，等關閉後（CLOSE_DIALOG）再補顯示
  if (isErrorDialogOpen || errorMessages.size === 0) {
    return
  }

  const text = [...errorMessages].join('\n\n──────────\n\n')
  errorMessages.clear()
  isErrorDialogOpen = true

  const { alert } = useConfirmDialog()
  alert({ title: '錯誤', text, confirmButtonText: '確定', dialogProps: { width: '420px' } })
}

const scheduleFlush = (delay: number = BURST_FLUSH_DELAY) => {
  if (flushTimer) {
    clearTimeout(flushTimer)
  }
  flushTimer = setTimeout(flushErrorMessages, delay)
}

// 彈窗關閉後重置旗標；若期間又累積了新錯誤，等關閉過場結束再顯示一個彙整彈窗
eventEmitter.on(CommonEvent.CLOSE_DIALOG, () => {
  isErrorDialogOpen = false
  if (errorMessages.size > 0) {
    scheduleFlush(REOPEN_AFTER_CLOSE_DELAY)
  }
})

const showErrorDialog = (text: string) => {
  errorMessages.add(text)
  scheduleFlush()
}

const handleApiStatus = async <T>(apiFunction: () => Promise<T>, options = { showAlert: true }): Promise<T> => {
  try {
    const response = await apiFunction()
    return response
  } catch (error: any) {
    console.error('API request failed:', error)

    if (options.showAlert) {
      const status = error.response?.status

      if (DETAIL_ERROR_CODES.has(status)) {
        const message = error.response?.data?.message ?? '發生錯誤'
        const details = error.response?.data?.details ? formatDetails(error.response.data.details) : ''
        const text = details ? `${message}\n\n${details}` : message
        showErrorDialog(text)
      } else if (NOTIFICATION_ERROR_CODES.has(status)) {
        const errorMsg = error.response?.data?.title ?? '未知錯誤'
        ElNotification({
          title: '失敗',
          message: errorMsg,
          type: 'error',
          position: 'bottom-right'
        })
      }
    }

    throw error
  }
}

export { handleApiStatus }
